<template>
  <base-widget>
    <base-heading class="mt-2">
      {{ title }}
    </base-heading>
    <hr
      style="width: 100%"
      class="my-2"
    >
    <template v-if="outreachs.length === 0 && !loading">
      <empty
        :title="emptyTitle"
        :subtitle="emptySubtitle"
        min-height="230px"
      />
    </template>
    <template v-else>
      <!-- Outreach list -->
      <template v-if="loading">
        <base-skeleton
          v-for="n in 5"
          :key="n"
          :opacity="0 + (1 /n)"
          class="py-4 my-0"
        >
          <div class="d-flex align-items-center mr-3">
            <div class="w-50 mr-5">
              <div
                class="skeleton__item py-2"
                style="max-width: 60%"
              />
              <div class="d-flex justify-content-start align-items-center">
                <div
                  class="skeleton__item mr-2"
                  style="max-width: 50%; margin-top: 0.5rem"
                />
                <div
                  class="skeleton__item"
                  style="max-width: 30%"
                />
              </div>
            </div>
            <div class="d-flex w-50 ml-5 justify-content-between align-items-center">
              <div
                v-for="m in 4"
                :key="`item_skel_${m}`"
                class="skeleton__item"
                :style="`max-width: 50px; ${m === 1 ? 'margin-top: 0.5rem;' : ''}`"
              />
              <div
                class="skeleton__item py-3 ml-2"
                style="max-width: 100px"
              />
            </div>
          </div>
        </base-skeleton>
      </template>
      <template v-else>
        <div
          v-for="(item, index) in items"
          :key="`stylist_outreach_list_${index}`"
          style="padding-top: 5px; padding-bottom: 5px;"
        >
          <out-reach-cell
            :item="item"
            :show-sender="showSender"
            @button-clicked="onViewButtonClicked(item)"
          />
        </div>
        <div
          class="base-pagination d-flex justify-content-center mt-3"
        >
          <base-pagination
            v-model="page"
            :count="outreachsCount"
            :page-size="pageSize"
            :hide-if-no-pages="false"
            @pageChange="pageChange"
          />
        </div>
      </template>
    </template>
  </base-widget>
</template>

<script>
import WhichBrowser from '@/components/mixins/whichbrowser';
import { EDIT_DISPLAY } from '@/_utilities/Enums';

export default {
  mixins: [WhichBrowser],
  props: {
    outreachs: {
      type: Array,
      default: () => [],
    },
    outreachsCount: {
      type: Number,
      default: 0,
    },
    pageSize: {
      type: Number,
      default: 12,
    },
    title: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyTitle: {
      type: String,
      default: '',
    },
    emptySubtitle: {
      type: String,
      default: '',
    },
    eventsPercentage: {
      type: Boolean,
      default: true,
    },
    showSender: {
      type: Boolean,
      default: false,
    },
    uniqueEvents: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    page: 1,
  }),
  computed: {
    items() {
      const DEFAULT_BACKGROUND_IMAGE = ''; // FIXME
      return this.outreachs.map((item) => ({
        pk: item.pk,
        contact_type: item.edit?.display || (item.appointment ? EDIT_DISPLAY.APPOINTMENT : undefined),
        clients: this.formatClients(item),
        events: this.formatEvents(item),
        backgroundImage: item.edit?.hero || DEFAULT_BACKGROUND_IMAGE,
        time: item.time_iso,
        subject: item.edit?.subject || item.appointment?.subject || 'Untitled',
        sender: item.sender.user,
        appointment: item.appointment,
      }));
    },
    trackedPage() {
      return this.$store.state.outreachs.page;
    },
  },
  created() {
    this.page = this.trackedPage ? this.trackedPage : 1;
  },
  methods: {
    /**
     * Formats the client section for specific displays (email, pdf, etc.)
     */
    formatClients(item) {
      // If we have no name at all, return this default String;
      const clientName = (item.sent_to?.display?.trim() ?? '') || this.$t('clients.singleClient');

      if (item.edit?.display === EDIT_DISPLAY.APPOINTMENT) {
        const invitees = item.appointment.invitations;
        // Separate invitees from anons
        const nAnons = invitees.filter((i) => i.customer === null).length;
        const nCustomers = invitees.filter((i) => i.customer).length;

        const anonsLabel = nAnons > 0 ? ` + ${nAnons} anonymous participant${nAnons > 1 ? 's' : ''}` : '';
        let customersLabel = '';
        if (nCustomers > 0) {
          if (nCustomers === 1) {
            const { customer } = invitees[0];
            customersLabel = `${customer.first_name} ${customer.last_name}`.trim();
            customersLabel = customersLabel.length ? customersLabel : this.$t('clients.singleClient');
          } else {
            customersLabel = `${nCustomers} Clients`;
          }
        }
        return `With: ${customersLabel}${anonsLabel}`;
      }
      if ([EDIT_DISPLAY.LINK, EDIT_DISPLAY.PDF].includes(item.edit?.display)) {
        return `Created for: ${clientName || `${item.sent_count} clients`}`;
      }
      return `Sent to: ${item.sent_count === 1 ? clientName : `${item.sent_count} clients`}`;
    },
    formatEvents(item) { // Retrieves and structures the events for specific displays
      const events = [];
      if (item.edit?.display === EDIT_DISPLAY.APPOINTMENT) {
        // can be invite or post-live
        // TODO: Implement post-live!
        return [];
        // let duration = '';
        // if (item.duration?.hours) {
        //   duration += `${item.duration.hours}h `;
        // }
        // duration += item.duration ? `${item.duration.minutes}m` : '--';
        // events.push({ label: 'Duration', value: duration });
        // return events;
      }
      if ([EDIT_DISPLAY.EMAIL, EDIT_DISPLAY.LINK, EDIT_DISPLAY.WECHAT, EDIT_DISPLAY.EMBED].includes(item.edit?.display)) {
        events.push({ label: 'Opens', value: item.opens });
      }
      events.push({ label: 'Clicks', value: item.clicks });
      return events;
    },
    pageChange(newpage) {
      this.page = newpage;
      this.$store.commit('outreachs/setPage', newpage);
      this.$emit('page-change', {
        page: this.page,
        page_size: this.pageSize,
      });
    },
    nOpens(outreach) {
      return this.uniqueEvents ? outreach.unique_opens : outreach.opens;
    },
    nClicks(outreach) {
      return this.uniqueEvents ? outreach.unique_clicks : outreach.clicks;
    },
    onViewButtonClicked(outreach) {
      const outreachPk = outreach.pk;
      const { trackedPage } = this;
      this.$store.dispatch('outreachs/reset', this.outreachId);
      this.$store.commit('outreachs/setPage', trackedPage);
      if (outreach.contact_type === 'appointment') {
        this.$router.push(`/reports/appointment/${outreach.appointment.pk}/stats/`);
        return;
      }
      this.$router.push(`/reports/outreach/${outreachPk}/stats/`);
    },
  },
};
</script>

<style lang="scss" scoped>
.outreach {
  &__count{
    min-width: 70px;
    padding: 0 0.75rem;
    text-align: center;
  }
  &__button {
    text-transform: uppercase;
    border: solid 0.75px #aaa;
    font-size: 0.6875rem;
    border-radius: 0;
    color: #aaa;
    white-space: nowrap;

    &.is-chrome {
      border: none;
      box-shadow: 0 0 0 0.75px #aaa;
    }
  }
  &__detail{
    color: $gray-light-2;
    font-size: 0.6875rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__sender{
    /*width: 126px;*/
    display: inline-block;
    margin-right: 1rem;
    cursor: pointer;

  }
}
</style>
